import { Divider, Drawer, Grid, Typography } from '@material-ui/core';
import { CadastroCriApiRequest, InstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest, GrupoAcesso, Perfil } from 'common/genericTypes';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, MdSpace, SmSpace, TextField } from 'm2m.components';
import { Dispatch, SetStateAction, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import { addAlert } from 'store/modules/snackBar';
import CriParser from 'utils/parsers/criParser';

export function FormularioDrawerEdicao({
  open,
  setOpen,
  handleSubmit,
  codigo,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleSubmit: () => void;
  codigo: string;
}) {
  const dispatch = useDispatch();
  const [documento, setDocumento] = useState<File | undefined>();
  const [justificativa, setJustificativa] = useState<string>('');

  const caracteristicas = useSelector((state: RootState) => state.criCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.criCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.criCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.criCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.criCadastro.emissor);
  const criComercialId = useSelector((state: RootState) => state.criCadastro.caracteristicas.id);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);
    const isUsuarioOperacoesMaster = useSelector(
    (state: RootState) =>
      state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes &&
      state.auth.usuario.participante.perfil === Perfil.Master);
  const criApi = useSelector((state: RootState) => state.criApi);

  const handleUpdateFileToStore = (attachments: Attachment[]): void => {
    const newAttachment = attachments?.[0].file;
    setDocumento(newAttachment);
  };

  const submitPutCriEdicao = async (
    criComercialId: string,
    criComercial: CadastroCriApiRequest,
  ): Promise<string> => {
    try {
      if (justificativa) {
        const result = await criApi.putEditarCri(
          dispatch,
          criComercialId,
          criComercial,
          justificativa,
          documento,
          true
        );
        return result.data;
      } else throw 'O campo de justificativa é obrigatório';
    } catch (error: any) {
      console.log(error);
      dispatch(addAlert({ message: error, severity: 'error' }));
    }
    return codigo;
  };

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cri,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  const submitCriDocument = async (criId: string, emissionType: string, file: File): Promise<UploadDocResponse[]> => {
    try {
      return criApi.putUploadDoc(dispatch, criId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const handleEdicaoSubmit = async () => {
    try {
      if (caracteristicas.numeroEmissao && caracteristicas.numeroEmissaoOriginal && 
        Number(caracteristicas.numeroEmissao) !== Number(caracteristicas.numeroEmissaoOriginal)) {
      const numeroEmissaoReservado = await reservarNumeroEmissao();
      if (!numeroEmissaoReservado) return;
    }

      const cri: CadastroCriApiRequest = CriParser.stateToCadastroCriApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );
      if (!criComercialId) throw 'Erro ao editar o CRI.';
      
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          const uploadResponse = await submitCriDocument(criComercialId, document.type, document.file);
          if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
        }
      }
      
      if (isUsuarioOperacoesMaster)
        {
          if (caracteristicas.id) {
            for (const documentToDelete of uploadDocumentos.documentsToDelete) {
              documentToDelete.remoteFileInfo?.id &&
              (await criApi.deleteUploadDoc(dispatch, criComercialId, documentToDelete.remoteFileInfo.id, true));
            }
          }
        }
      const result = await submitPutCriEdicao(criComercialId, cri);
      if (!result) throw 'Erro ao editar o CRI';

      dispatch(addAlert({ message: `Instrumento Financeiro ${result} editado com sucesso.`, severity: 'success' }));
      handleSubmit();

    } catch (error: any) {
      dispatch(addAlert({ message: error.message, severity: 'error' }));
      throw error;
    }
  };

  const handleClose = () => {
    setDocumento(undefined);
    setJustificativa('');
    setOpen(false);
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ style: { padding: '40px', maxWidth: 460 } }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h3">{'Edição'}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <MdSpace />
          <Typography variant="body2">{`Tem certeza que quer editar o instrumento financeiro ${codigo}?`}</Typography>
          <SmSpace />
          <TextField
            value={justificativa}
            label="Justificativa"
            onChange={(value) => setJustificativa(value)}
            multiline
            rows={4}
            required
          />
          <SmSpace />
          <UploadDocs
            multipleFiles={false}
            inputAttachments={[]}
            onChange={handleUpdateFileToStore}
          />
          <MdSpace />
          <Button
            onClick={handleEdicaoSubmit}
            disabled={!justificativa}
            variant="contained"
            color="primary"
            fullWidth
          >
            {'Confirmar Edição'}
          </Button>
          <SmSpace />
          <Button onClick={handleClose} variant="contained" color="error" fullWidth>
            {'Cancelar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
}
